/**
 * Recurring Tasks Page
 * AIDLC Administrative Backend
 *
 * 3-tab page for managing recurring agent tasks:
 *   1. Configuration — budget, email, delegates, engine toggle
 *   2. Task List — create, edit, submit, approve, pause, run-now
 *   3. Execution History — past runs with status, tokens, result
 *
 * Personas: Executive, Ops
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtDateTime, fmtTokens, fmtNum } from '../../utils/formatters';
import type {
  RecurringTask,
  RecurringTaskExecution,
  RecurringTaskConfig,
  RecurringTaskScheduleType,
  RecurringTaskStatus,
  ExecutionStatus,
} from '../../services/orchestrationService';
import {
  getRecurringTasks,
  createRecurringTask,
  updateRecurringTask,
  deleteRecurringTask,
  submitRecurringTask,
  approveRecurringTask,
  rejectRecurringTask,
  pauseRecurringTask,
  resumeRecurringTask,
  runRecurringTaskNow,
  getAllRecurringExecutions,
  getRecurringTaskConfig,
  updateRecurringTaskConfig,
} from '../../services/orchestrationService';
import { AgentSelect } from '../shared/AgentSelect';
import { apiErrorMessage } from '../../utils/apiError';

// ── Constants ────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type ActiveTab = 'config' | 'tasks' | 'history';

// ── Status Badge ─────────────────────────────────────────────────────

function StatusBadge({ status }: { status: RecurringTaskStatus }) {
  const map: Record<RecurringTaskStatus, { variant: 'neutral' | 'warning' | 'success' | 'info' | 'purple' | 'danger'; label: string }> = {
    draft: { variant: 'neutral', label: 'Draft' },
    pending_approval: { variant: 'warning', label: 'Pending Approval' },
    active: { variant: 'success', label: 'Active' },
    paused: { variant: 'info', label: 'Paused' },
    completed: { variant: 'purple', label: 'Completed' },
    archived: { variant: 'neutral', label: 'Archived' },
  };
  const { variant, label } = map[status] || map.draft;
  return <Badge variant={variant}>{label}</Badge>;
}

function ExecStatusBadge({ status }: { status: ExecutionStatus }) {
  const map: Record<ExecutionStatus, { variant: 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'purple'; label: string }> = {
    pending: { variant: 'neutral', label: 'Pending' },
    running: { variant: 'info', label: 'Running' },
    success: { variant: 'success', label: 'Success' },
    failure: { variant: 'danger', label: 'Failed' },
    skipped: { variant: 'warning', label: 'Skipped' },
    budget_exceeded: { variant: 'danger', label: 'Budget Exceeded' },
  };
  const { variant, label } = map[status] || map.pending;
  return <Badge variant={variant}>{label}</Badge>;
}

function ScheduleBadge({ task }: { task: RecurringTask }) {
  let label = '';
  switch (task.scheduleType) {
    case 'once':
      label = task.scheduledAt ? `Once: ${fmtDateTime(task.scheduledAt)}` : 'Once';
      break;
    case 'daily':
      label = `Daily at ${task.timeOfDay}`;
      break;
    case 'weekly':
      label = `Weekly ${DAYS_OF_WEEK[task.dayOfWeek ?? 1]} at ${task.timeOfDay}`;
      break;
    case 'monthly':
      label = `Monthly day ${task.dayOfMonth ?? 1} at ${task.timeOfDay}`;
      break;
  }
  return <Badge variant="info">{label}</Badge>;
}

// ── Main Component ───────────────────────────────────────────────────

export function RecurringTasksPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('tasks');
  const [tasks, setTasks] = useState<RecurringTask[]>([]);
  const [executions, setExecutions] = useState<RecurringTaskExecution[]>([]);
  const [config, setConfig] = useState<RecurringTaskConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState<RecurringTask | null>(null);
  const [rejectModalTaskId, setRejectModalTaskId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Config form state
  const [configForm, setConfigForm] = useState<Partial<RecurringTaskConfig>>({});
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [tasksData, execData, configData] = await Promise.all([
        getRecurringTasks(),
        getAllRecurringExecutions(50),
        getRecurringTaskConfig(),
      ]);
      setTasks(tasksData);
      setExecutions(execData);
      setConfig(configData);
      setConfigForm(configData);
    } catch (err) {
      console.error('[RecurringTasks] Failed to load:', err);
      setError('Failed to load recurring tasks. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Actions ────────────────────────────────────────────────────────

  async function handleAction(action: () => Promise<unknown>) {
    try {
      await action();
      await loadData();
    } catch (err) {
      console.error('[RecurringTasks] Action failed:', err);
      setError(`Action failed: ${(err as Error).message}`);
    }
  }

  async function handleSaveConfig() {
    setConfigSaving(true);
    setConfigSaved(false);
    try {
      const updated = await updateRecurringTaskConfig(configForm);
      setConfig(updated);
      setConfigForm(updated);
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 3000);
    } catch (err) {
      setError(`Failed to save config: ${(err as Error).message}`);
    } finally {
      setConfigSaving(false);
    }
  }

  // ── Loading / Error States ─────────────────────────────────────────

  if (isLoading) {
    return <LoadingSpinner message="Loading recurring tasks..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">{error}</p>
        <button onClick={() => { setError(null); loadData(); }} className="mt-2 text-xs text-red-600 hover:underline">Retry</button>
      </div>
    );
  }

  // ── Tab Counts ─────────────────────────────────────────────────────

  const activeTasks = tasks.filter(t => t.status === 'active').length;
  const pendingApproval = tasks.filter(t => t.status === 'pending_approval').length;

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Total Tasks" value={tasks.length} />
        <StatCard label="Active" value={activeTasks} color="text-green-600" />
        <StatCard label="Pending Approval" value={pendingApproval} color="text-yellow-600" />
        <StatCard label="Budget Used" value={`${fmtTokens(config?.tokensUsedThisMonth ?? 0)} / ${fmtTokens(config?.monthlyTokenBudget ?? 100000)}`} />
        <StatCard label="Engine" value={config?.enabled ? 'Running' : 'Stopped'} color={config?.enabled ? 'text-green-600' : 'text-red-600'} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
        {([
          { key: 'tasks' as ActiveTab, label: 'Task List', count: tasks.length },
          { key: 'history' as ActiveTab, label: 'Execution History', count: executions.length },
          { key: 'config' as ActiveTab, label: 'Configuration', count: null },
        ]).map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              activeTab === key ? 'bg-ldaja-blue text-white' : 'text-gray-500 hover:text-ldaja-black hover:bg-gray-50'
            }`}
          >
            {label}
            {count !== null && (
              <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                activeTab === key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
              }`}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'tasks' && (
        <TaskListTab
          tasks={tasks}
          onAction={handleAction}
          onCreateClick={() => { setEditingTask(null); setShowCreateModal(true); }}
          onEdit={(task) => { setEditingTask(task); setShowCreateModal(true); }}
          onReject={(id) => { setRejectModalTaskId(id); setRejectReason(''); }}
        />
      )}
      {activeTab === 'history' && <ExecutionHistoryTab executions={executions} tasks={tasks} />}
      {activeTab === 'config' && config && (
        <ConfigurationTab
          configForm={configForm}
          setConfigForm={setConfigForm}
          onSave={handleSaveConfig}
          saving={configSaving}
          saved={configSaved}
        />
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <TaskFormModal
          task={editingTask}
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            if (editingTask) {
              await updateRecurringTask(editingTask.id, data);
            } else {
              await createRecurringTask(data as Parameters<typeof createRecurringTask>[0]);
            }
            setShowCreateModal(false);
            await loadData();
          }}
        />
      )}

      {/* Reject Modal */}
      {rejectModalTaskId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-sm font-semibold text-ldaja-black mb-3">Reject Task</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 h-24 resize-none"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRejectModalTaskId(null)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
              <button
                onClick={async () => {
                  if (rejectModalTaskId) {
                    await handleAction(() => rejectRecurringTask(rejectModalTaskId, rejectReason));
                    setRejectModalTaskId(null);
                  }
                }}
                className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Stat Card ────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{label}</div>
      <div className={`text-lg font-bold mt-1 ${color || 'text-ldaja-black'}`}>
        {typeof value === 'number' ? fmtNum(value) : value}
      </div>
    </div>
  );
}

// ── Task List Tab ────────────────────────────────────────────────────

function TaskListTab({
  tasks,
  onAction,
  onCreateClick,
  onEdit,
  onReject,
}: {
  tasks: RecurringTask[];
  onAction: (action: () => Promise<unknown>) => void;
  onCreateClick: () => void;
  onEdit: (task: RecurringTask) => void;
  onReject: (id: string) => void;
}) {
  if (tasks.length === 0) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <button onClick={onCreateClick} className="px-3 py-1.5 bg-ldaja-blue text-white rounded-md text-xs font-medium hover:bg-ldaja-blue/90">
            + New Task
          </button>
        </div>
        <EmptyState
          title="No Recurring Tasks"
          description="Create your first recurring task to automate agent work. Tasks go through CEO approval before becoming active."
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={onCreateClick} className="px-3 py-1.5 bg-ldaja-blue text-white rounded-md text-xs font-medium hover:bg-ldaja-blue/90">
          + New Task
        </button>
      </div>

      <div className="space-y-2">
        {tasks.map((task) => (
          <Card key={task.id}>
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <StatusBadge status={task.status} />
                  <ScheduleBadge task={task} />
                  <span className="text-[10px] text-gray-400 font-mono">{task.id}</span>
                </div>
                <h3 className="text-sm font-semibold text-ldaja-black">{task.title}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Agent: <span className="font-medium">{task.assignedAgent}</span>
                  {' '}&middot;{' '}
                  Budget: <span className="font-medium">{fmtTokens(task.tokenBudgetPerRun)}/run</span>
                  {' '}&middot;{' '}
                  Runs: <span className="font-medium">{task.totalRuns}</span>
                </p>
                {task.description && (
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{task.description}</p>
                )}
                {task.rejectionReason && (
                  <p className="text-xs text-red-500 mt-1">Rejected: {task.rejectionReason}</p>
                )}
                {task.nextRunAt && (
                  <p className="text-[10px] text-gray-400 mt-1">Next run: {fmtDateTime(task.nextRunAt)}</p>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-1.5 ml-4 flex-shrink-0">
                {task.status === 'draft' && (
                  <>
                    <ActionBtn label="Edit" color="blue" onClick={() => onEdit(task)} />
                    <ActionBtn label="Submit" color="blue" onClick={() => onAction(() => submitRecurringTask(task.id))} />
                    <ActionBtn label="Delete" color="red" onClick={() => onAction(() => deleteRecurringTask(task.id))} />
                  </>
                )}
                {task.status === 'pending_approval' && (
                  <>
                    <ActionBtn label="Approve" color="green" onClick={() => onAction(() => approveRecurringTask(task.id))} />
                    <ActionBtn label="Reject" color="red" onClick={() => onReject(task.id)} />
                  </>
                )}
                {task.status === 'active' && (
                  <>
                    <ActionBtn label="Run Now" color="blue" onClick={() => onAction(() => runRecurringTaskNow(task.id))} />
                    <ActionBtn label="Pause" color="yellow" onClick={() => onAction(() => pauseRecurringTask(task.id))} />
                  </>
                )}
                {task.status === 'paused' && (
                  <>
                    <ActionBtn label="Edit" color="blue" onClick={() => onEdit(task)} />
                    <ActionBtn label="Resume" color="green" onClick={() => onAction(() => resumeRecurringTask(task.id))} />
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ActionBtn({ label, color, onClick }: { label: string; color: 'blue' | 'green' | 'red' | 'yellow'; onClick: () => void }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
    green: 'bg-green-50 text-green-700 hover:bg-green-100',
    red: 'bg-red-50 text-red-700 hover:bg-red-100',
    yellow: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
  };
  return (
    <button onClick={onClick} className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${colors[color]}`}>
      {label}
    </button>
  );
}

// ── Execution History Tab ────────────────────────────────────────────

function ExecutionHistoryTab({ executions, tasks }: { executions: RecurringTaskExecution[]; tasks: RecurringTask[] }) {
  const taskMap = new Map(tasks.map(t => [t.id, t]));

  if (executions.length === 0) {
    return <EmptyState title="No Executions Yet" description="Execution history will appear here once recurring tasks start running." />;
  }

  return (
    <Card>
      <div className="overflow-x-auto -mx-4 -mb-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-4 py-2 font-medium text-gray-500">Status</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Task</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Agent</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Triggered</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Tokens</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Result</th>
            </tr>
          </thead>
          <tbody>
            {executions.map((exec) => {
              const task = taskMap.get(exec.recurringTaskId);
              return (
                <tr key={exec.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-4 py-2.5"><ExecStatusBadge status={exec.status} /></td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-ldaja-black">{task?.title ?? exec.recurringTaskId}</div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{exec.agentId}</td>
                  <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(exec.triggeredAt)}</td>
                  <td className="px-4 py-2.5 font-mono text-gray-600">{fmtTokens(exec.tokensUsed)}</td>
                  <td className="px-4 py-2.5 text-gray-500 max-w-xs truncate">
                    {exec.error ? (
                      <span className="text-red-500">{exec.error}</span>
                    ) : exec.result ? (
                      exec.result
                    ) : (
                      <span className="text-gray-300">--</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Configuration Tab ────────────────────────────────────────────────

function ConfigurationTab({
  configForm,
  setConfigForm,
  onSave,
  saving,
  saved,
}: {
  configForm: Partial<RecurringTaskConfig>;
  setConfigForm: React.Dispatch<React.SetStateAction<Partial<RecurringTaskConfig>>>;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
}) {
  const update = (key: keyof RecurringTaskConfig, value: unknown) => {
    setConfigForm(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Engine & Budget */}
      <Card title="Engine & Budget" subtitle="Control the recurring task engine and monthly token budget.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Engine Status</label>
            <button
              onClick={() => update('enabled', !configForm.enabled)}
              className={`px-4 py-2 rounded-md text-xs font-medium transition-colors ${
                configForm.enabled
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {configForm.enabled ? 'Enabled (Running)' : 'Disabled (Stopped)'}
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Check Interval (seconds)</label>
            <input
              type="number"
              min={10}
              max={3600}
              value={configForm.checkIntervalSeconds ?? 60}
              onChange={(e) => update('checkIntervalSeconds', parseInt(e.target.value) || 60)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Monthly Token Budget</label>
            <input
              type="number"
              min={0}
              step={10000}
              value={configForm.monthlyTokenBudget ?? 100000}
              onChange={(e) => update('monthlyTokenBudget', parseInt(e.target.value) || 100000)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              Used this month: {fmtTokens(configForm.tokensUsedThisMonth ?? 0)}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Budget Reset Day</label>
            <input
              type="number"
              min={1}
              max={28}
              value={configForm.budgetResetDay ?? 1}
              onChange={(e) => update('budgetResetDay', parseInt(e.target.value) || 1)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30"
            />
          </div>
        </div>
      </Card>

      {/* Email Configuration */}
      <Card title="Email Alerts" subtitle="Configure SMTP to receive email notifications when tasks run.">
        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={configForm.emailEnabled ?? false}
                onChange={(e) => update('emailEnabled', e.target.checked)}
                className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/30"
              />
              <span className="text-xs font-medium text-gray-600">Enable email alerts</span>
            </label>
          </div>
          {configForm.emailEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="SMTP Host" value={configForm.smtpHost ?? ''} onChange={(v) => update('smtpHost', v)} placeholder="smtp.gmail.com" />
              <FormField label="SMTP Port" value={String(configForm.smtpPort ?? 587)} onChange={(v) => update('smtpPort', parseInt(v) || 587)} placeholder="587" />
              <FormField label="SMTP User" value={configForm.smtpUser ?? ''} onChange={(v) => update('smtpUser', v)} placeholder="user@example.com" />
              <FormField label="SMTP Password" value={configForm.smtpPass ?? ''} onChange={(v) => update('smtpPass', v)} placeholder="Enter password" type="password" />
              <FormField label="From Email" value={configForm.fromEmail ?? ''} onChange={(v) => update('fromEmail', v)} placeholder="noreply@ldaja.com" />
              <FormField label="From Name" value={configForm.fromName ?? ''} onChange={(v) => update('fromName', v)} placeholder="AIDLC Recurring Tasks" />
            </div>
          )}
        </div>
      </Card>

      {/* Delegates */}
      <Card title="Authorized Delegates" subtitle="Email addresses authorized to create recurring tasks (in addition to CEO/Dan).">
        <div>
          <textarea
            value={(configForm.delegates ?? []).join('\n')}
            onChange={(e) => update('delegates', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
            placeholder="Enter one email per line..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 h-24 resize-none font-mono"
          />
          <p className="text-[10px] text-gray-400 mt-1">
            {(configForm.delegates ?? []).length} delegate(s) configured. CEO approval is still required for all tasks.
          </p>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-ldaja-blue text-white rounded-md text-xs font-medium hover:bg-ldaja-blue/90 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        {saved && <span className="text-xs text-green-600 font-medium">Configuration saved successfully.</span>}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = 'text' }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30"
      />
    </div>
  );
}

// ── Task Form Modal ──────────────────────────────────────────────────

function TaskFormModal({
  task,
  onClose,
  onSave,
}: {
  task: RecurringTask | null;
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [form, setForm] = useState({
    title: task?.title ?? '',
    description: task?.description ?? '',
    scheduleType: (task?.scheduleType ?? 'weekly') as RecurringTaskScheduleType,
    scheduledAt: task?.scheduledAt ?? '',
    dayOfWeek: task?.dayOfWeek ?? 1,
    dayOfMonth: task?.dayOfMonth ?? 1,
    timeOfDay: task?.timeOfDay ?? '09:00',
    assignedAgent: task?.assignedAgent ?? 'todd',
    agentPrompt: task?.agentPrompt ?? '',
    projectId: task?.projectId ?? '',
    tokenBudgetPerRun: task?.tokenBudgetPerRun ?? 5000,
    alertOnSuccess: task?.alertOnSuccess ?? true,
    alertOnFailure: task?.alertOnFailure ?? true,
    alertChannels: task?.alertChannels ?? { inApp: true, email: false },
    alertEmails: task?.alertEmails?.join(', ') ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!form.title || !form.assignedAgent || !form.agentPrompt) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        ...form,
        alertEmails: form.alertEmails.split(',').map(s => s.trim()).filter(Boolean),
      });
    } catch (err) {
      console.error('[RecurringTasks] save refused', err);
      setSaveError(apiErrorMessage(err, 'The task could not be saved. Check your connection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
        <h3 className="text-sm font-semibold text-ldaja-black mb-4">{task ? 'Edit Task' : 'Create Recurring Task'}</h3>

        <div className="space-y-4">
          {/* Title & Description */}
          <FormField label="Title *" value={form.title} onChange={(v) => setForm(f => ({ ...f, title: v }))} placeholder="e.g. Weekly Dependency Check" />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What does this task do?"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 h-16 resize-none"
            />
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Schedule Type *</label>
              <select
                value={form.scheduleType}
                onChange={(e) => setForm(f => ({ ...f, scheduleType: e.target.value as RecurringTaskScheduleType }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30"
              >
                <option value="once">One-time</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {form.scheduleType === 'once' && (
              <FormField label="Scheduled At (ISO)" value={form.scheduledAt} onChange={(v) => setForm(f => ({ ...f, scheduledAt: v }))} placeholder="2026-02-15T09:00:00Z" />
            )}
            {form.scheduleType === 'weekly' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Day of Week</label>
                <select
                  value={form.dayOfWeek}
                  onChange={(e) => setForm(f => ({ ...f, dayOfWeek: parseInt(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30"
                >
                  {DAYS_OF_WEEK.map((day, i) => (
                    <option key={i} value={i}>{day}</option>
                  ))}
                </select>
              </div>
            )}
            {form.scheduleType === 'monthly' && (
              <FormField label="Day of Month" value={String(form.dayOfMonth)} onChange={(v) => setForm(f => ({ ...f, dayOfMonth: parseInt(v) || 1 }))} placeholder="1" />
            )}

            {form.scheduleType !== 'once' && (
              <FormField label="Time of Day" value={form.timeOfDay} onChange={(v) => setForm(f => ({ ...f, timeOfDay: v }))} placeholder="09:00" />
            )}
          </div>

          {/* Agent & Prompt */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="recurring-assigned-agent" className="block text-xs font-medium text-gray-600 mb-1">Assigned Agent *</label>
              <AgentSelect
                id="recurring-assigned-agent"
                value={form.assignedAgent}
                onChange={(agentId) => setForm(f => ({ ...f, assignedAgent: agentId }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30"
              />
            </div>
            <FormField label="Token Budget Per Run" value={String(form.tokenBudgetPerRun)} onChange={(v) => setForm(f => ({ ...f, tokenBudgetPerRun: parseInt(v) || 5000 }))} placeholder="5000" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Agent Prompt *</label>
            <textarea
              value={form.agentPrompt}
              onChange={(e) => setForm(f => ({ ...f, agentPrompt: e.target.value }))}
              placeholder="The prompt/instructions that will be given to the agent when this task runs..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 h-24 resize-none"
            />
          </div>

          <FormField label="Project ID (optional)" value={form.projectId} onChange={(v) => setForm(f => ({ ...f, projectId: v }))} placeholder="aidlc-admin" />

          {/* Alert Configuration */}
          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-xs font-semibold text-gray-600 mb-3">Alert Settings</h4>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.alertOnSuccess} onChange={(e) => setForm(f => ({ ...f, alertOnSuccess: e.target.checked }))} className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/30" />
                <span className="text-xs text-gray-600">Alert on success</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.alertOnFailure} onChange={(e) => setForm(f => ({ ...f, alertOnFailure: e.target.checked }))} className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/30" />
                <span className="text-xs text-gray-600">Alert on failure</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.alertChannels.inApp} onChange={(e) => setForm(f => ({ ...f, alertChannels: { ...f.alertChannels, inApp: e.target.checked } }))} className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/30" />
                <span className="text-xs text-gray-600">In-app notifications</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.alertChannels.email} onChange={(e) => setForm(f => ({ ...f, alertChannels: { ...f.alertChannels, email: e.target.checked } }))} className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/30" />
                <span className="text-xs text-gray-600">Email alerts</span>
              </label>
            </div>
            {form.alertChannels.email && (
              <div className="mt-3">
                <FormField label="Alert Emails (comma-separated)" value={form.alertEmails} onChange={(v) => setForm(f => ({ ...f, alertEmails: v }))} placeholder="dan@ldaja.com, ceo@ldaja.com" />
              </div>
            )}
          </div>
        </div>

        {saveError && (
          <p role="alert" className="mt-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {saveError}
          </p>
        )}

        {/* Modal Footer */}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.title || !form.agentPrompt}
            className="px-4 py-1.5 bg-ldaja-blue text-white rounded-md text-xs font-medium hover:bg-ldaja-blue/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : task ? 'Update Task' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
